//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';
import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import _ from 'underscore';
import { Checkbox } from 'unison';

import { IGallery } from '@apis/galleries/galleriesApiModels';
import { Alert } from '@components/atoms/alert/Alert';
import {
  MediumTooltip,
  SmallTooltip,
} from '@components/molecules/tooltip/Tooltip';
import { GalleriesTrackingIds } from '@galleries/constants/galleriesConstants';
import GalleryContainerId from '@galleries/models/GalleryContainerId';
import getGalleriesClientType, {
  GalleriesClientType,
} from '@galleries/selectors/getGalleriesClientType';
import { getGalleriesForCheckboxList } from '@galleries/selectors/getGalleriesForCheckboxList';
import { isSelfServeGallery } from '@galleries/utils/galleryTypeUtils';
import { ICuralateStore } from '@models/AppState';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import ReelId from '@models/ReelId';
import { Mixins } from '@theme/mixins';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';
import { isDefined } from '@utils/filterPredicates';

enum ALL_GALLERIES_CHECKBOX_STATE {
  CHECKED = 'checked',
  UNCHECKED = 'unchecked',
  INDETERMINATE = 'indeterminate',
}

interface IOwnProps {
  configurationId?: number;
  areNoGalleriesSelected: boolean;
  areNoCheckboxesSelected: boolean;
  gallerySelectionState: PojoSet<ReelId>;
  gallerySelected: (
    galleryContainerId: GalleryContainerId,
    configurationId?: number,
  ) => void;
  showHeaderText?: boolean;
  isLoading: boolean;
  disabledGalleryTypes?: Partial<Record<ExperienceContainerType, string>>;
  isBazaarvoiceSyndicationSelected: boolean;
  toggleBazaarvoiceSyndication: (configurationId?: number) => void;
  shouldDisableBazaarvoiceSyndicationCheckbox: boolean;
}

const mapStateToProps = (state: ICuralateStore) => ({
  doesItemHaveRightsExpirySet: isDefined(
    state.uiaModal.selectedItem?.selectedItemMetadata?.rightsPermissions
      ?.expirationTime,
  ),
  galleries: getGalleriesForCheckboxList(state),
  isCustomGalleriesClient:
    getGalleriesClientType(state) === GalleriesClientType.custom,
  shouldUnselectAllByDefault:
    !!state.session.features.galleries_bulk_approval_deselect_all,
  isBazaarvoiceSyndicationEnabled:
    getIsBazaarvoiceNetworkSyndicationFrontendEnabled(state),
});

type Props = IOwnProps & ReturnType<typeof mapStateToProps>;

interface IOwnState {
  isPdpUnselected: boolean;
  isCategoryUnselected: boolean;
}

export class GalleriesCheckboxList extends React.Component<Props, IOwnState> {
  public static defaultProps = {
    disabledGalleryTypes: {},
  };

  constructor(props: Props) {
    super(props);

    const { shouldUnselectAllByDefault } = props;

    this.state = {
      isPdpUnselected: shouldUnselectAllByDefault,
      isCategoryUnselected: shouldUnselectAllByDefault,
    };
  }

  public componentDidUpdate(prevProps: Props) {
    const didFinishLoading = prevProps.isLoading && !this.props.isLoading;
    if (didFinishLoading) {
      this.unselectAllGalleriesIfNecessary();

      if (
        !this.props.shouldUnselectAllByDefault &&
        this.props.isBazaarvoiceSyndicationSelected &&
        (this.props.shouldDisableBazaarvoiceSyndicationCheckbox ||
          this.props.doesItemHaveRightsExpirySet)
      ) {
        this.props.toggleBazaarvoiceSyndication(this.props.configurationId);
      }
    }
  }

  public unselectAllGalleriesIfNecessary() {
    const {
      configurationId,
      galleries,
      gallerySelected,
      gallerySelectionState,
      shouldUnselectAllByDefault,
      toggleBazaarvoiceSyndication,
    } = this.props;

    if (shouldUnselectAllByDefault) {
      const selectedGalleries = galleries.filter((gallery) => {
        return PojoSet.has(gallerySelectionState, gallery.reelId);
      });

      selectedGalleries.forEach((gallery) =>
        gallerySelected(gallery.containerId, configurationId),
      );

      if (this.props.isBazaarvoiceSyndicationSelected) {
        toggleBazaarvoiceSyndication(configurationId);
      }
    }
  }

  public render() {
    const {
      areNoCheckboxesSelected,
      isCustomGalleriesClient,
      isLoading,
      showHeaderText,
    } = this.props;

    return (
      <>
        {showHeaderText && (
          <div className='cu-text-subsection cu-mb-1'>
            Select the Galleries you want to add to:
          </div>
        )}
        {areNoCheckboxesSelected && !isLoading && (
          <WarningText>
            You must select a Gallery before continuing.
          </WarningText>
        )}
        <>
          <ListContainer>
            {isLoading ? this.getLoadingPlaceholders() : this.getCheckboxes()}
          </ListContainer>
          {isCustomGalleriesClient && (
            <Alert className='cu-mt-2' type='info'>
              We recommend adding content to your Product Detail Page and
              Category Page galleries to ensure product coverage across your
              product and category pages.
            </Alert>
          )}
        </>
      </>
    );
  }

  private getDisabledReelIds() {
    const { disabledGalleryTypes, galleries } = this.props;

    return galleries
      .filter((gallery) => {
        return (
          isSelfServeGallery(gallery) &&
          disabledGalleryTypes?.[gallery.containerType]
        );
      })
      .map((gallery) => gallery.reelId);
  }

  private getSelectAllGalleriesState(): ALL_GALLERIES_CHECKBOX_STATE {
    const { areNoGalleriesSelected, galleries, gallerySelectionState } =
      this.props;

    const totalNumGalleries = galleries.length;
    const numSelectedGalleries = PojoSet.toArray(gallerySelectionState).length;
    const numDisabledGalleries = this.getDisabledReelIds().length;

    const areAllGalleriesSelected =
      numSelectedGalleries + numDisabledGalleries === totalNumGalleries;

    if (areAllGalleriesSelected) {
      return ALL_GALLERIES_CHECKBOX_STATE.CHECKED;
    } else if (areNoGalleriesSelected) {
      return ALL_GALLERIES_CHECKBOX_STATE.UNCHECKED;
    } else {
      return ALL_GALLERIES_CHECKBOX_STATE.INDETERMINATE;
    }
  }

  private handleClick(
    gallery: IGallery,
    isProductGallery: boolean,
    isCategoryGallery: boolean,
  ) {
    const { configurationId, gallerySelected, gallerySelectionState } =
      this.props;

    const { isCategoryUnselected, isPdpUnselected } = this.state;

    if (isProductGallery || isCategoryGallery) {
      if (PojoSet.has(gallerySelectionState, gallery.reelId)) {
        this.setState({
          isPdpUnselected: isProductGallery ? true : isPdpUnselected,
          isCategoryUnselected: isCategoryGallery ? true : isCategoryUnselected,
        });
      } else {
        this.setState({
          isPdpUnselected: isProductGallery ? false : isPdpUnselected,
          isCategoryUnselected: isCategoryGallery
            ? false
            : isCategoryUnselected,
        });
      }
    }

    gallerySelected(gallery.containerId, configurationId);
  }

  private handleSelectAllGalleriesClick() {
    const { galleries, gallerySelectionState } = this.props;

    const isSelectingAll =
      this.getSelectAllGalleriesState() !==
      ALL_GALLERIES_CHECKBOX_STATE.CHECKED;

    const disabledGalleryReelIds = this.getDisabledReelIds();
    const nonDisabledGalleries = galleries.filter(
      (gallery) => !disabledGalleryReelIds.includes(gallery.reelId),
    );

    const [nonDisabledSelectedGalleries, nonDisabledDeselectedGalleries] = _(
      nonDisabledGalleries,
    ).partition((gallery) => {
      return PojoSet.has(gallerySelectionState, gallery.reelId);
    });

    const galleriesToActOn = isSelectingAll
      ? nonDisabledDeselectedGalleries
      : nonDisabledSelectedGalleries;

    galleriesToActOn?.forEach((gallery) => {
      const isProductGallery =
        isSelfServeGallery(gallery) &&
        gallery.containerType === ExperienceContainerType.PRODUCT;

      const isCategoryGallery =
        isSelfServeGallery(gallery) &&
        gallery.containerType === ExperienceContainerType.CATEGORY;

      this.handleClick(gallery, isProductGallery, isCategoryGallery);
    });
  }

  private getAllGalleriesCheckbox() {
    const onSelectAllClick = () => this.handleSelectAllGalleriesClick();

    const selectAllState = this.getSelectAllGalleriesState();

    const isChecked = selectAllState === ALL_GALLERIES_CHECKBOX_STATE.CHECKED;
    const isIndeterminate =
      selectAllState === ALL_GALLERIES_CHECKBOX_STATE.INDETERMINATE;

    return (
      <TopLevelCheckboxItem>
        <StyledCheckbox
          handleClick={onSelectAllClick}
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
          trackingId={GalleriesTrackingIds.GALLERY_DISTRIBUTE_TRACKING_ID}
        />
        <GalleryLabel onClick={onSelectAllClick}>Galleries</GalleryLabel>
      </TopLevelCheckboxItem>
    );
  }

  private getGalleriesCheckboxes() {
    const { disabledGalleryTypes, galleries, gallerySelectionState } =
      this.props;

    return galleries.map((gallery) => {
      const disabledText =
        isSelfServeGallery(gallery) &&
        disabledGalleryTypes?.[gallery.containerType];

      if (disabledText) {
        return this.renderDisabledGalleryCheckbox(disabledText, gallery);
      } else {
        return this.renderGalleryCheckbox(
          gallery,
          PojoSet.has(gallerySelectionState, gallery.reelId),
        );
      }
    });
  }

  private getBazaarvoiceSyndicationCheckbox() {
    const {
      configurationId,
      doesItemHaveRightsExpirySet,
      isBazaarvoiceSyndicationSelected,
      shouldDisableBazaarvoiceSyndicationCheckbox,
      toggleBazaarvoiceSyndication,
    } = this.props;
    const shouldDisableSelection =
      shouldDisableBazaarvoiceSyndicationCheckbox ||
      doesItemHaveRightsExpirySet;

    const handleOnClick = () => {
      if (shouldDisableSelection) {
        return;
      }
      toggleBazaarvoiceSyndication(configurationId);
    };

    return (
      <MediumTooltip
        content={
          !doesItemHaveRightsExpirySet
            ? 'This item was distributed to you via the Visual Syndication Network.'
            : 'This item cannot be distributed to Visual Syndication Network because an expiry date is set on the content rights.'
        }
        isDisabled={!shouldDisableSelection}
        target={
          <>
            <TopLevelCheckboxItem>
              <StyledCheckbox
                handleClick={handleOnClick}
                isChecked={isBazaarvoiceSyndicationSelected}
                isDisabled={shouldDisableSelection}
                trackingId={
                  GalleriesTrackingIds.GALLERY_BVNETWORK_DISTRIBUTE_TRACKING_ID
                }
              />
              <GalleryLabel
                isDisabled={shouldDisableSelection}
                onClick={handleOnClick}
              >
                Distribution
              </GalleryLabel>
            </TopLevelCheckboxItem>
            <SecondLevelCheckboxItem key='bazaarvoiceSyndication'>
              <StyledCheckbox
                handleClick={handleOnClick}
                isChecked={isBazaarvoiceSyndicationSelected}
                isDisabled={shouldDisableSelection}
                trackingId={
                  GalleriesTrackingIds.GALLERY_BVNETWORK_DISTRIBUTE_TRACKING_ID
                }
              />
              <GalleryLabel
                isDisabled={shouldDisableSelection}
                onClick={handleOnClick}
              >
                Visual Syndication Network
              </GalleryLabel>
            </SecondLevelCheckboxItem>
          </>
        }
      />
    );
  }

  private getCheckboxes() {
    const { galleries, isBazaarvoiceSyndicationEnabled } = this.props;
    const hasGalleries = galleries.length > 0;

    return (
      <>
        {hasGalleries && this.getAllGalleriesCheckbox()}
        {this.getGalleriesCheckboxes()}
        {isBazaarvoiceSyndicationEnabled &&
          this.getBazaarvoiceSyndicationCheckbox()}
      </>
    );
  }

  private readonly renderDisabledGalleryCheckbox = (
    disabledText: string,
    gallery: IGallery,
  ) => {
    return (
      <SmallTooltip
        key={gallery.containerId}
        content={<span>{disabledText}</span>}
        placement='right'
        target={
          <TooltipTargetContainer>
            <SecondLevelCheckboxItem key={gallery.containerId}>
              <StyledCheckbox
                handleClick={() => undefined}
                isChecked={false}
                isDisabled={true}
              />
              <GalleryLabel isDisabled={true}>
                {gallery.displayName}
              </GalleryLabel>
            </SecondLevelCheckboxItem>
          </TooltipTargetContainer>
        }
      />
    );
  };

  private readonly galleryUnselectedAlert = (
    galleryName: string,
  ) => `To ensure content coverage across your website, we
      recommend adding content to ${galleryName} galleries.`;

  private readonly renderGalleryCheckbox = (
    gallery: IGallery,
    isChecked: boolean,
  ) => {
    const { isCategoryUnselected, isPdpUnselected } = this.state;

    const isProductGallery =
      isSelfServeGallery(gallery) &&
      gallery.containerType === ExperienceContainerType.PRODUCT;
    const isCategoryGallery =
      isSelfServeGallery(gallery) &&
      gallery.containerType === ExperienceContainerType.CATEGORY;
    const onClick = () =>
      this.handleClick(gallery, isProductGallery, isCategoryGallery);

    return (
      <React.Fragment key={gallery.containerId}>
        <SecondLevelCheckboxItem>
          <StyledCheckbox handleClick={onClick} isChecked={isChecked} />
          <GalleryLabel onClick={onClick}>{gallery.displayName}</GalleryLabel>
        </SecondLevelCheckboxItem>
        {isProductGallery && isPdpUnselected && (
          <Alert className='cu-mt-2' type='info'>
            {this.galleryUnselectedAlert('Product Detail Page')}
          </Alert>
        )}
        {isCategoryGallery && isCategoryUnselected && (
          <Alert className='cu-mt-2' type='info'>
            {this.galleryUnselectedAlert('Category Page')}
          </Alert>
        )}
      </React.Fragment>
    );
  };

  private getLoadingPlaceholders() {
    return (
      <LoadingPlaceholderContainers>
        <MediumPlaceholder className='loading-element gradient' />
        <LargePlaceholder className='loading-element gradient' />
        <SmallPlaceholder className='loading-element gradient' />
      </LoadingPlaceholderContainers>
    );
  }
}

export default connect(mapStateToProps)(GalleriesCheckboxList);

const TooltipTargetContainer = styled.div(
  ({ theme }) => `
  display: inline-block;
  padding-right: ${theme.spacings[2]}
`,
);

const TopLevelCheckboxItem = styled.div`
  display: flex;
  align-items: center;
  ${({ theme }) => `
    padding: ${theme.spacings[1]} 0;
    margin: ${theme.spacings[1]} 0;
  `}
`;

const SecondLevelCheckboxItem = styled(TopLevelCheckboxItem)`
  ${({ theme }) => `
    margin: ${theme.spacings[1]} 0 ${theme.spacings[1]} ${theme.spacings[3]};
  `}
`;

const StyledCheckbox = styled(Checkbox)`
  flex-shrink: 0;
`;

const GalleryLabel = styled.label<{ isDisabled?: boolean }>(
  ({ isDisabled = false, theme }) => `
  margin-bottom: 0;
  padding-left: ${theme.spacings[3]};
  flex-shrink: 1;
  ${isDisabled && `color: ${theme.colors.aliases.disabled};`}
`,
);

const ListContainer = styled.div`
  overflow-y: auto;
`;

const MediumPlaceholder = styled.div`
  grid-column: 1 / 45;
`;

const WarningText = styled.div`
  ${Mixins.Text.Body}
  ${({ theme }) => `
    margin-bottom: ${theme.spacings[2]};
    && {
      color: ${theme.colors.aliases.error};
    }
  `}
`;

const LargePlaceholder = styled.div`
  grid-column: 1 / 60;
`;

const SmallPlaceholder = styled.div`
  grid-column: 1 / 40;
`;

const LoadingPlaceholderContainers = styled.div`
  display: grid;
  grid-template-columns: repeat(100, 1fr);
  grid-template-rows: repeat(3, 20px);
  grid-row-gap: ${({ theme }) => theme.spacings[3]};
  margin-top: ${({ theme }) => theme.spacings[3]};
`;
